import React, { useEffect, useMemo, useRef, useState } from "react";

// others
import { Loading } from "./index";
import Button from "../../../../molecules/Button";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Form, Radio, Space, message } from "antd";
import { useParams } from "react-router-dom";
import { translateService, getTenantDetailByEmailService } from "../../ducks/services";
import { DateField, InputField, InputRadio, ReactSelectField } from "../../../../atoms/FormElement";
import {
  radioOptions,
  filterPayload,
  marital_status,
  signatoryPayload,
  filterDropdownValue,
  filterKycSelectList,
  KYC_CONTENT,
  kycInitValue,
  handleKycFields,
  datePlaceholderddmmyyFormat,
} from "../utils";
import { updateBooking, getTenantDetailByEmail, getBookingDetail } from "../../ducks/actions";
import { Info } from "../../../../../assets/svg";
import { getValidationProps, isValidEmail } from "../../../../libs/form";
import { Booking, TenantType, UsageType } from "../../../../../types/bookings";
import { TenantDetailForm } from "../tenant-details/TenantDetailForm";
import { SignatoryFields } from "../tenant-details/SignatoryFields";
import { prepareFormData } from "../tenant-details/utils/prepareFormData";
import { resolveCommercialCompanyName } from "../tenant-details/utils/resolveCommercialCompanyName";
import { match, P } from "ts-pattern";

interface IProps {
  parentState: (obj: object) => void;
  kycDetails: Array<object>;
}

let initialState = {
  visible: false,
  isSignatory: "",
};

let timeOutId: ReturnType<typeof setTimeout>;
let aTimeOutId: ReturnType<typeof setTimeout>;

const index = (props: IProps) => {

  const dispatch: any = useDispatch();
  const { leaseId = "" } = useParams();

  const { configData, bookingForm, bookingDetails } = useSelector((state: any) => state.newBooking);

  const usagesType = bookingDetails?.propertyDetails?.usagesType;
  const usageType = (usagesType?.toLowerCase() || "") as UsageType;
  const isCommercial = usageType === "commercial";

  const tenantType: TenantType = useMemo(
    () =>
      bookingDetails?.tenantDetails?.tenantIdentityType ||
      bookingForm?.tenantDetails?.tenantIdentityType ||
      bookingForm?.tenantIdentityType,
    [bookingDetails, bookingForm],
  );

  const methods = useForm<any>({
    defaultValues: {
      tenantType,
      isSignatory: bookingForm?.isSignatory || bookingDetails?.isSignatory || "yes",
    },
  });

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const watchedFields = watch();
  const lastLookedUpTenantEmail = useRef<string | null>(null);

  const tenantEmail = useMemo(() => {
    return match<[UsageType, TenantType]>([usageType, tenantType])
      .with(["residential", P._], () => watchedFields?.email)
      .with(["commercial", "company"], () => watchedFields?.commercial?.company?.email)
      .with(["commercial", "individual"], () => watchedFields?.commercial?.individual?.email)
      .otherwise(() => undefined);
  }, [usageType, tenantType, watchedFields]);

  const hasCompanyTenantOptions =
    Array.isArray(bookingForm?.companyTenants) && bookingForm.companyTenants.length > 0;

  const signatoryEmail = useWatch({
    control,
    name: isCommercial ? "signatoryUser.email" : "signatoryEmail",
  });
  const name = useWatch({ control, name: "name" });
  const signatoryName = useWatch({
    control,
    name: isCommercial ? "signatoryUser.name" : "signatoryName",
  });

  const isBooking = Object.keys(bookingDetails).length > 0;

  const [{ visible, isSignatory }, setState] = useState(initialState);
 
  const latestSignatoryLookupId = useRef(0);
  const previousSignatoryEmail = useRef("");

  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));

  const showSignatoryFields = useMemo(() => {
    if (usageType === "residential") return isSignatory === "no";
    if (usageType === "commercial" && tenantType === "company") return true;
    if (usageType === "commercial" && tenantType === "individual") return isSignatory === "no";
    return isSignatory === "no";
  }, [usageType, tenantType, isSignatory]);

  const showSignatoryRadioButton = useMemo(() => {
    if (usageType === "commercial" && tenantType === "company") return false;
    return true;
  }, [usageType, tenantType]);

  const normalizeSelectValue = (value: unknown) => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    return (value as { value?: string; label?: string })?.value || (value as { label?: string })?.label;
  };

   const tenantFormValues = useMemo(() => {
    const hasBookingForm = bookingForm && Object.entries(bookingForm).length > 0;
    const hasBookingDetails = bookingDetails && Object.keys(bookingDetails).length > 0;
    if (!hasBookingForm && !hasBookingDetails) return { tenantType };

    const currentValues = getValues();
    const { tenantDetails } = bookingDetails as Booking;

    const signatoryFromStore =
      bookingForm?.signatoryUser ?? (hasBookingDetails ? (bookingDetails as Booking)?.signatoryUser : undefined);

    const activeTenantType = tenantDetails?.tenantIdentityType ?? tenantType;

    const defaultFields = {
      tenantType: activeTenantType,
      isSignatory,
      ...(usageType === "commercial" && activeTenantType === "company"
        ? { companyTenants: bookingForm?.companyTenants || (tenantDetails as any)?.companyTenants || [] }
        : {}),
      ...(activeTenantType === "company" || (activeTenantType === "individual" && isSignatory === "no")
        ? { signatoryUser: signatoryFromStore }
        : {}),
    };


    const mappedFields = match<[string, string]>([usageType, activeTenantType])
      .with(["commercial", "company"], () => {
        const companyTenantsList = bookingForm?.companyTenants || (tenantDetails as any)?.companyTenants || [];
        const companyTenantId = (tenantDetails as any)?.companyTenantId;

        return {
          commercial: {
            company: {
              email: tenantDetails?.email || currentValues?.commercial?.company?.email || bookingForm?.email || "",
              name: resolveCommercialCompanyName({
                companyTenantId,
                companyTenants: companyTenantsList,
                currentName: currentValues?.commercial?.company?.name,
                bookingFormName: bookingForm?.name,
              }),
              phone: tenantDetails?.companyPhone || currentValues?.commercial?.company?.phone || bookingForm?.mobile,
              address: tenantDetails?.address || currentValues?.commercial?.company?.address || bookingForm?.address,
              city: tenantDetails?.city || currentValues?.commercial?.company?.city || bookingForm?.city,
              ...(tenantDetails?.country || currentValues?.commercial?.company?.country || bookingForm?.country
                ? {
                    country: filterDropdownValue(
                      normalizeSelectValue(tenantDetails?.country) ||
                        currentValues?.commercial?.company?.country?.value ||
                        normalizeSelectValue(bookingForm?.country),
                      configData?.nationalityList,
                    ),
                  }
                : {}),
              poBox: tenantDetails?.poBox || currentValues?.commercial?.company?.poBox || bookingForm?.poBox,
              licenseNo:
                tenantDetails?.tradeLicense || currentValues?.commercial?.company?.licenseNo || bookingForm?.licenseNo,
                workPhone: tenantDetails?.workPhone || "",
            },
          },
        };
      })
      .with(["commercial", "individual"], () => ({
        commercial: {
          individual: {
            email: tenantDetails?.email || currentValues?.commercial?.individual?.email || bookingForm?.email || "",
            name: tenantDetails?.tenantNameEN || currentValues?.commercial?.individual?.name || bookingForm?.name || "",
            mobile: tenantDetails?.mobileNumber || currentValues?.commercial?.individual?.mobile || bookingForm?.mobile,
            designation:
              tenantDetails?.designation ||
              currentValues?.commercial?.individual?.designation ||
              bookingForm?.designation,
            company: tenantDetails?.company || currentValues?.commercial?.individual?.company || bookingForm?.company,
          },
        },
      }))
      .with(["residential", P._], () => {
        // Get firstName and lastName - prioritize form values, then API data, then split old name
        const firstName = currentValues?.firstName || bookingForm?.firstName || tenantDetails?.firstName || "";
        const lastName = currentValues?.lastName || bookingForm?.lastName || tenantDetails?.lastName || "";

        // If firstName/lastName exist, use them; otherwise fallback to splitting old name
        let finalFirstName = firstName;
        let finalLastName = lastName;

        if (!finalFirstName && !finalLastName) {
          // Fallback: split old name for backward compatibility
          const tenantNameEN = tenantDetails?.tenantNameEN || currentValues?.name || bookingForm?.name || "";
          const nameParts = tenantNameEN.trim().split(/\s+/);
          finalFirstName = nameParts[0] || "";
          finalLastName = nameParts.slice(1).join(" ") || "";
        }

        // Always generate name from firstName and lastName (never use old name directly)
        const generatedName = [finalFirstName, finalLastName].filter(Boolean).join(" ") || "";

        return {
          email: tenantDetails?.email || currentValues?.email || bookingForm?.email || "",
          firstName: finalFirstName,
          lastName: finalLastName,
          name: generatedName,
          arabicName: tenantDetails?.tenantNameAR || currentValues?.arabicName || bookingForm?.arabicName,
          mobile: tenantDetails?.mobileNumber || currentValues?.mobile || bookingForm?.mobile,
          // Prefer API/bookingForm values over any existing form values (prevents default "971" overriding API)
          countryCode: bookingForm?.countryCode || tenantDetails?.countryCode || currentValues?.countryCode || "971",
          workPhone: currentValues?.workPhone || bookingForm?.workPhone || tenantDetails?.workPhone || "",
          nationality: filterDropdownValue(
            tenantDetails?.nationality || currentValues?.nationality?.value || bookingForm?.nationality,
            configData?.nationalityList,
          ),
          maritalStatus: filterDropdownValue(
            tenantDetails?.maritalStatus || currentValues?.maritalStatus?.value || bookingForm?.maritalStatus,
            marital_status,
          ),
        };
      })
      .otherwise(() => defaultFields);

    return { ...defaultFields, ...mappedFields };
  }, [bookingForm, bookingDetails, tenantType, isSignatory, usageType, configData, getValues]);

  useEffect(() => {
    if (isBooking) {
      const nextSignatoryValue =
        isCommercial && tenantType === "company"
          ? "no"
          : bookingDetails?.isSignatory || bookingForm?.isSignatory || "yes";

      updateState({ isSignatory: nextSignatoryValue });
      setValue("isSignatory", nextSignatoryValue);
      setValue("tenantType", tenantType);
    }
  }, [isBooking, isCommercial, tenantType, bookingDetails, bookingForm, setValue]);

  useEffect(() => {
    if (!tenantFormValues) return;
    reset(tenantFormValues, { keepDefaultValues: false });
  }, [tenantFormValues, reset]);



 const dispatchTenantLookup = (email: string) => {
  const normalized = email.trim().toLowerCase();
  if (!isValidEmail(normalized)) return;
  const shouldFetchMissingCompanyOptions =
    usageType === "commercial" && tenantType === "company" && !hasCompanyTenantOptions;
  if (lastLookedUpTenantEmail.current === normalized && !shouldFetchMissingCompanyOptions) {
    return;
  }
  lastLookedUpTenantEmail.current = normalized;
  dispatch(getTenantDetailByEmail(email, false, { usageType, tenantType }));
};
useEffect(() => {
  if (!tenantEmail) return;
  dispatchTenantLookup(tenantEmail);
}, [tenantEmail, hasCompanyTenantOptions, usageType, tenantType]);
  useEffect(() => {
    const normalizedSignatoryEmail = signatoryEmail?.trim()?.toLowerCase() || "";
    const normalizedTenantEmail = tenantEmail?.trim()?.toLowerCase() || "";

    if (!normalizedSignatoryEmail || normalizedSignatoryEmail === previousSignatoryEmail.current) return;

    previousSignatoryEmail.current = normalizedSignatoryEmail;

    if (!isValidEmail(normalizedSignatoryEmail)) return;

    if (usageType === "commercial" && tenantType === "company" && normalizedSignatoryEmail === normalizedTenantEmail) {
      return;
    }

    getSignatoryTenant(normalizedSignatoryEmail);
  }, [signatoryEmail, tenantEmail, usageType, tenantType]);

  useEffect(() => {
    if (!isCommercial && name && name.length > 1) {
      clearTimeout(timeOutId);

      timeOutId = setTimeout(async () => {
        const response = await translateService(name);
        if (response?.translatedText) setValue("arabicName", response?.translatedText);
      }, 2000);
    }

    if (signatoryName && signatoryName.length > 1) {
      clearTimeout(aTimeOutId);

      aTimeOutId = setTimeout(async () => {
        const response = await translateService(signatoryName);
        if (response?.translatedText) {
          setValue(isCommercial ? "signatoryUser.arabicName" : "signatoryArabicName", response?.translatedText);
        }
      }, 2000);
    }
  }, [name, signatoryName, isCommercial, setValue]);

  const getSignatoryTenant = async (emailValue: string) => {
    const lookupId = latestSignatoryLookupId.current + 1;
    latestSignatoryLookupId.current = lookupId;

    try {
      const response = await getTenantDetailByEmailService(emailValue);
      if (lookupId !== latestSignatoryLookupId.current) return;

      if (response) {
        if (isCommercial) {
          if (response?.email) setValue("signatoryUser.email", response.email);
          if (response?.tenantNameEN) setValue("signatoryUser.name", response.tenantNameEN);
          if (response?.tenantNameAR) setValue("signatoryUser.arabicName", response.tenantNameAR);
          if (response?.mobileNumber) setValue("signatoryUser.mobile", response.mobileNumber);
          const designation = response?.designationAsPerVisa || response?.designation;
          if (designation) setValue("signatoryUser.designation", designation);
          if (response?.company) setValue("signatoryUser.company", response.company);
        } else {
          setValue("signatoryEmail", response?.email);
          setValue("signatoryName", response?.tenantNameEN);
          setValue("signatoryArabicName", response?.tenantNameAR);
          setValue("signatoryMobile", response?.mobileNumber);
          setValue("designation", response?.designationAsPerVisa);
          setValue("company", response?.company);
        }
      }
    } catch (error: any) {}
  };

  const onSubmit = async (data: any) => {
    updateState({ visible: true });

    try {
      if (isCommercial) {
        const { companyTenants: _companyTenants, ...dataWithoutCompanyTenants } = data;

        const formData = prepareFormData({
          rawData: { ...dataWithoutCompanyTenants, tenantType },
          usageType: "commercial",
          isBooking: true,
          unitCode: bookingDetails?.propertyDetails?.unitCode,
          kycDetails: props?.kycDetails || [],
        });

        if (props?.kycDetails?.length > 0) {
          formData.submitKyc = "yes";
        }

        const response = await dispatch(
          updateBooking({
            leaseId,
            body: formData,
          }),
        );

        setTimeout(() => {
          dispatch(getBookingDetail(leaseId));
        }, 400);

        message.success(response.data.message);
        updateState({ visible: false });
        props.parentState({
          modalPayload: { iKey: "", title: "", extraText: "", open: false },
        });
        return;
      }

      const { companyTenants: _companyTenants, ...dataWithoutCompanyTenants } = data || getValues();
      let dataCopy = { ...dataWithoutCompanyTenants };

      const residentialFields = {
        nationality: dataCopy.nationality.value,
        maritalStatus: dataCopy.maritalStatus.value,
      };

      dataCopy = {
        ...dataCopy,
        ...residentialFields,
      };

      if (props?.kycDetails?.length > 0) {
        dataCopy = handleKycFields(dataCopy);
        dataCopy = { ...dataCopy, submitKyc: "yes" };
      }

      if (dataCopy["isSignatory"] === "no") {
        dataCopy["signatoryUser"] = signatoryPayload(dataCopy);
      }

      const response = await dispatch(
        updateBooking({
          leaseId,
          body: filterPayload(dataCopy, usageType),
        }),
      );

      setTimeout(() => {
        dispatch(getBookingDetail(leaseId));
      }, 400);
  

      
      message.success(response.data.message);
      updateState({ visible: false });
      props.parentState({
        modalPayload: { iKey: "", title: "", extraText: "", open: false },
      });
    } catch (error: any) {
      updateState({ visible: false });
      props.parentState({ visible: false });
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  };

  

  const signatoryRequired = showSignatoryFields;

  const renderKycFields = () =>
    props?.kycDetails?.map((item: any, kycIndex: number) => {
      const tooltip = {
        title: KYC_CONTENT,
        icon: <Info width="16px" height="16px" />,
      };

      return (
        <React.Fragment key={`${item.name}-${kycIndex}`}>
          {["text", "number", "numberWithComma"].includes(item.type) && (
            <Col span={8}>
              <InputField
                control={control}
                label={item.label}
                fieldname={`kyc-${item.name}`}
                iProps={{ placeholder: "Placeholder" }}
                initValue={kycInitValue(item.name, bookingDetails?.kycDetails)}
                {...{ tooltip }}
              />
            </Col>
          )}

          {["date"].includes(item.type) && (
            <Col span={8}>
              <DateField
                control={control}
                label={item.label}
                fieldname={`kyc-${item.name}`}
                iProps={{
                  format: datePlaceholderddmmyyFormat,
                  placeholder: datePlaceholderddmmyyFormat,
                }}
                initValue={kycInitValue(item.name, bookingDetails?.kycDetails)}
                {...{ tooltip }}
              />
            </Col>
          )}

          {["select"].includes(item.type) && (
            <Col span={8}>
              <ReactSelectField
                control={control}
                label={item.label}
                fieldname={`kyc-${item.name}`}
                iProps={{ placeholder: "Select" }}
                selectOption={filterKycSelectList(item.options[0])}
                initValue={filterDropdownValue(
                  kycInitValue(item.name, bookingDetails?.kycDetails),
                  filterKycSelectList(item.options[0]),
                )}
                {...{ tooltip }}
              />
            </Col>
          )}
        </React.Fragment>
      );
    });

  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        name="bookingform"
        scrollToFirstError
        className="bf-main"
        onFinish={handleSubmit(onSubmit)}
      >
        <Loading visible={visible} />

        <Col span={24}>
          <TenantDetailForm usageType={usageType} tenantType={tenantType} />

          <Row gutter={24}>{renderKycFields()}</Row>

          {showSignatoryRadioButton && (
            <Row gutter={24}>
              <Col span={8}>
                {isCommercial ? (
                  <InputRadio
                    fieldname="isSignatory"
                    label="Is the tenant the authorized signatory?"
                    control={control}
                    setValue={setValue}
                    {...getValidationProps({ fieldName: "isSignatory", errors, isRequired: true })}
                    onChange={(e: any) => updateState({ isSignatory: e.target.value })}
                    options={
                      <Row gutter={[16, 16]}>
                        {radioOptions.map((option, i) => (
                          <Col span={6} key={`${option.value}-${i}`}>
                            <Radio value={option.value}>{option.label}</Radio>
                          </Col>
                        ))}
                      </Row>
                    }
                  />
                ) : (
                  <InputRadio
                    isRequired={true}
                    control={control}
                    fieldname="isSignatory"
                    validate={errors.isSignatory && "error"}
                    label="Is the tenant the authorized signatory?"
                    validMessage={errors.isSignatory && errors.isSignatory.message}
                    rules={{
                      required: "This field is required",
                    }}
                    disabled={true}
                    onChange={(e: any) => updateState({ isSignatory: e.target.value })}
                    options={
                      <Row gutter={[16, 16]}>
                        {radioOptions.map((option, i) => (
                          <Col span={6} key={`${option.value}-${i}`}>
                            <Radio value={option.value}>{option.label}</Radio>
                          </Col>
                        ))}
                      </Row>
                    }
                  />
                )}
              </Col>
            </Row>
          )}

          {signatoryRequired &&
            (isCommercial ? (
              <SignatoryFields usageType="commercial" />
            ) : (
              <Col span={24}>
                <p className="td-label">Additional Information for Signatory</p>
                <div className="ft-divider" />

                <Row gutter={24}>
                  <Col span={8}>
                    <InputField
                      control={control}
                      setValue={setValue}
                      label="Email Address"
                      fieldname="signatoryEmail"
                      isRequired={signatoryRequired}
                      valueGot={bookingDetails?.signatoryUser?.email}
                      validate={errors.signatoryEmail && "error"}
                      iProps={{ placeholder: "john.smith@email.com" }}
                      rules={{
                        required: "Email Address Required",
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: "Enter valid Email Address",
                        },
                      }}
                      validMessage={errors.signatoryEmail && errors.signatoryEmail.message}
                    />
                  </Col>

                  <Col span={8}>
                    <InputField
                      control={control}
                      setValue={setValue}
                      label="Signatory Name"
                      fieldname="signatoryName"
                      isRequired={signatoryRequired}
                      valueGot={bookingDetails?.signatoryUser?.name}
                      iProps={{ placeholder: "John Smith" }}
                      validate={errors.signatoryName && "error"}
                      rules={{ required: "Please enter tenant name" }}
                      validMessage={errors.signatoryName && errors.signatoryName.message}
                    />
                  </Col>

                  <Col span={8}>
                    <InputField
                      control={control}
                      setValue={setValue}
                      label="Signatory Arabic Name"
                      isRequired={signatoryRequired}
                      fieldname="signatoryArabicName"
                      iProps={{ placeholder: "جون سميث" }}
                      valueGot={bookingDetails?.signatoryUser?.arabicName}
                      validate={errors.signatoryArabicName && "error"}
                      rules={{ required: "Please enter tenant arabic name " }}
                      validMessage={errors.signatoryArabicName && errors.signatoryArabicName.message}
                    />
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={8}>
                    <InputField
                      control={control}
                      setValue={setValue}
                      label="Mobile number"
                      fieldname="signatoryMobile"
                      isRequired={signatoryRequired}
                      valueGot={bookingDetails?.signatoryUser?.mobile}
                      validate={errors.signatoryMobile && "error"}
                      iProps={{
                        addonBefore: `+${
                          typeof bookingDetails?.signatoryUser?.countryCode === "string"
                            ? bookingDetails.signatoryUser.countryCode
                            : bookingDetails?.tenantDetails?.countryCode || bookingForm?.countryCode || "971"
                        }`,
                        placeholder: "554441212",
                      }}
                      validMessage={errors.signatoryMobile && errors.signatoryMobile.message}
                      rules={{
                        minLength: {
                          value: 9,
                          message: "Please enter valid mobile number, 9 digits required",
                        },
                        maxLength: {
                          value: 9,
                          message: "Please enter valid mobile number, 9 digits required",
                        },
                        required: "Please enter Mobile No.",
                      }}
                    />
                  </Col>

                  <Col span={8}>
                    <InputField
                      control={control}
                      label="Designation"
                      setValue={setValue}
                      fieldname="designation"
                      isRequired={signatoryRequired}
                      valueGot={
                        bookingDetails?.signatoryUser?.designation ||
                        bookingForm["designationAsPerVisa"] ||
                        bookingForm?.designation
                      }
                      iProps={{ placeholder: "Manager" }}
                      validate={errors.designation && "error"}
                      rules={{ required: "Please enter your designation." }}
                      validMessage={errors.designation && errors.designation.message}
                    />
                  </Col>

                  <Col span={8}>
                    <InputField
                      label="Company"
                      control={control}
                      fieldname="company"
                      setValue={setValue}
                      isRequired={signatoryRequired}
                      valueGot={bookingDetails?.signatoryUser?.company}
                      validate={errors.company && "error"}
                      iProps={{ placeholder: "Xyz Real Estate" }}
                      rules={{ required: "Please enter company name." }}
                      validMessage={errors.company && errors.company.message}
                    />
                  </Col>
                </Row>
              </Col>
            ))}

          <Row gutter={24} className="footer">
            <Space size="middle">
              <Button
                label="Cancel"
                className="bf-discard-button"
                onClick={() => props.parentState({ modalPayload: { open: false } })}
              />

              <Button type="submit" label="Update" className="cc-fotter_next-button" />
            </Space>
          </Row>
        </Col>
      </Form>
    </FormProvider>
  );
};

export default index;
